import { useState, useCallback, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import type { FiardleLeaderboardEntry } from '@/lib/api';

export function useFiardleLeaderboard(sortBy: string = 'wins') {
  const fetchIdRef = useRef(0);
  const [entries, setEntries] = useState<FiardleLeaderboardEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLeaderboard = useCallback(
    async (offset: number = 0) => {
      const id = ++fetchIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const result = await api.getFiardleLeaderboard({
          sortBy: sortBy as 'wins' | 'winRate' | 'streak',
          limit: 50,
          offset,
        });
        if (fetchIdRef.current !== id) return;
        setEntries(result.entries);
        setTotal(result.total);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch leaderboard');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [sortBy],
  );

  useEffect(() => {
    fetchLeaderboard();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchLeaderboard]);

  return { entries, total, loading, error, refetch: fetchLeaderboard };
}
